import { makeStyles } from "tss-react/mui"
import { Box, Card, CardActionArea, CardContent, Grid, Typography } from "@mui/material"

import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { StandardDialog } from "../../../../dialogs/StandardDialog"

import { TakeawayKitchen } from "../takeawayTypes"
import { useApiImageUrl } from "../useApiImageUrl"

const useStyles = makeStyles()((theme) => ({
    content: {
        padding: "1.5rem",
    },
    kitchenCard: {
        borderRadius: ".75rem",
        border: `1px solid ${theme.palette.grey[300]}`,
    },
    kitchenImage: {
        width: "100%",
        height: "8rem",
        objectFit: "cover",
        display: "block",
    },
    kitchenImagePlaceholder: {
        width: "100%",
        height: "8rem",
        backgroundColor: theme.palette.tailwind.slate[200],
    },
}))

type KitchenCardProps = Readonly<{
    kitchen: TakeawayKitchen
    onSelect: (kitchen: TakeawayKitchen) => void
    classes: ReturnType<typeof useStyles>["classes"]
}>

function KitchenCard({ kitchen, onSelect, classes }: KitchenCardProps) {
    const imageUrl = useApiImageUrl(kitchen.brandingDetails?.imageUrl)

    return (
        <Card className={classes.kitchenCard} elevation={0}>
            <CardActionArea onClick={() => onSelect(kitchen)}>
                {imageUrl ? (
                    <img src={imageUrl} className={classes.kitchenImage} alt={kitchen.name} />
                ) : (
                    <Box className={classes.kitchenImagePlaceholder} />
                )}
                <CardContent>
                    <Typography variant="subtitle1" fontWeight={600}>
                        {kitchen.name}
                    </Typography>
                    {kitchen.brandingDetails?.description && (
                        <Typography variant="body2" color="text.secondary">
                            {kitchen.brandingDetails.description}
                        </Typography>
                    )}
                    {(kitchen.streetName || kitchen.city) && (
                        <Typography variant="caption" color="text.secondary">
                            {[kitchen.streetName, kitchen.streetNumber, kitchen.city].filter(Boolean).join(", ")}
                        </Typography>
                    )}
                </CardContent>
            </CardActionArea>
        </Card>
    )
}

type TakeawayKitchenSelectorDialogProps = Readonly<{
    kitchens: TakeawayKitchen[]
    onSelect: (kitchen: TakeawayKitchen) => void
    onClose: () => void
}>

export function TakeawayKitchenSelectorDialog({ kitchens, onSelect, onClose }: TakeawayKitchenSelectorDialogProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    return (
        <StandardDialog
            open
            onClose={onClose}
            dialogTitle={l10n.getString("takeaway-kitchen-selector-title")}
            maxWidthProp="md"
        >
            <Box className={classes.content}>
                <Grid container spacing={2}>
                    {kitchens.map((kitchen, index) => (
                        <Grid item xs={12} sm={6} md={4} key={`${kitchen.id}-${index}`}>
                            <KitchenCard kitchen={kitchen} onSelect={onSelect} classes={classes} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </StandardDialog>
    )
}
